import { Observable, Subscription, race, timer, AsyncSubject } from 'rxjs';
import { mapTo } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { PblNgridComponent } from '@pebula/ngrid';

const DEFAULT_TIMEOUT = 1000 * 60 * 3; // 3 Minutes

export type ExportType = 'managed';

export interface ExportInstanceMeta {
  name: string;
  type: ExportType;
  title?: string;
  timeout?: number;
}

export class ExportInstance<T = any> {
  state: 'ready' | 'running' | 'complete' | 'error' | 'cancelled' | 'timeout' = 'ready';
  meta: ExportInstanceMeta;
  error: Error | undefined;
  data: T[];

  get isRunning(): boolean {
    return this.state === 'running';
  }

  completed = new AsyncSubject<Pick<ExportInstance, 'state' | 'error'>>();
  private subscription: Subscription;

  constructor(private obs$: Observable<any>, meta: ExportInstanceMeta) {
    this.meta = { timeout: DEFAULT_TIMEOUT, ...meta };
    this.init();
  }

  cancel(): void {
    if (this.state !== 'running') {
      throw new Error('Invalid state.');
    }
    this.state = 'cancelled';
    if (!this.subscription.closed) {
      this.subscription.unsubscribe();
    }
  }

  private init(): void {
    if (this.state !== 'ready') {
      throw new Error('Invalid State.');
    }
    this.state = 'running';

    this.subscription = race(timer(this.meta.timeout).pipe(mapTo('timeout')), this.obs$).subscribe(
      (result) => {
        if (this.state === 'running' && result === 'timeout') {
          this.state = 'timeout';
          this.completed.next(this);
          this.completed.complete();
        } else {
          this.data = result;
        }
      },
      (err) => {
        if (this.state === 'running') {
          this.state = 'error';
          this.error = err;
          this.completed.next(this);
          this.completed.complete();
        }
      },
      () => {
        if (this.state === 'running') {
          this.state = 'complete';
          this.completed.next(this);
          this.completed.complete();
        }
      }
    );
  }
}

@Injectable({ providedIn: 'root' })
export class SgTableExportManagerService {
  private managedExports = new Map<PblNgridComponent<any>, ExportInstance>();

  addManagedExport<T = any>(table: PblNgridComponent<T>, done: Observable<any>): ExportInstance<T> {
    const meta: ExportInstanceMeta = {
      name: '',
      type: 'managed',
      // title?: string;
      timeout: 1000 * 60 * 10, // 10 Minutes
    };
    const exportInstance = new ExportInstance<T>(done, meta);
    this.managedExports.set(table, exportInstance);
    exportInstance.completed.subscribe(() => this.managedExports.delete(table));
    return exportInstance;
  }
}
